import { environment } from 'src/environments/environment';

/**
 * Provider-agnostic OAuth2 (Authorization Code + PKCE) config registry.
 *
 * Adding a new provider = add one entry to OAUTH_PROVIDERS and a matching
 * client-ID slot in environment.oauthClientIds. No other code changes needed
 * for the client-side flow; the auth server must also know how to verify
 * the provider's code exchange.
 */

export type OAuthProviderId = 'discord' | 'google';

export interface OAuthProviderConfig {
  readonly id: OAuthProviderId;
  readonly displayName: string;
  /** Provider's authorize endpoint — where the user is redirected to consent. */
  readonly authorizeUrl: string;
  /** Space-separated OAuth scopes to request. */
  readonly scope: string;
  /** Client ID registered in the provider's developer console. */
  readonly clientId: string;
  /** Extra static query params for the authorize URL (e.g. Google's prompt=select_account). */
  readonly extraAuthorizeParams?: Readonly<Record<string, string>>;
  /**
   * When true, generate an OIDC `nonce` per login attempt, send it in the authorize URL,
   * and pass it to the auth server so the server can verify it against the ID token's
   * `nonce` claim after code exchange. Only meaningful for OpenID Connect providers
   * (scope contains `openid`).
   */
  readonly useOidcNonce?: boolean;
}

export const OAUTH_PROVIDERS: Readonly<Record<OAuthProviderId, OAuthProviderConfig>> = {
  discord: {
    id: 'discord',
    displayName: 'Discord',
    authorizeUrl: 'https://discord.com/api/oauth2/authorize',
    scope: 'identify email',
    clientId: environment.oauthClientIds.discord,
  },
  google: {
    id: 'google',
    displayName: 'Google',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    scope: 'openid email profile',
    clientId: environment.oauthClientIds.google,
    extraAuthorizeParams: { access_type: 'online', prompt: 'select_account' },
    useOidcNonce: true,
  },
};

export function isOAuthProviderId(value: string | null | undefined): value is OAuthProviderId {
  return !!value && Object.prototype.hasOwnProperty.call(OAUTH_PROVIDERS, value);
}

/**
 * Redirect URI handed to providers and posted back to the auth server during code
 * exchange. Must EXACTLY match what's registered in each provider console
 * (origin + path), so we derive it at runtime from window.location.origin.
 */
export function buildRedirectUri(providerId: OAuthProviderId): string {
  return `${window.location.origin}/auth/callback/${providerId}`;
}

/** Providers that have a client ID configured — used to render only available buttons. */
export function availableProviders(): OAuthProviderConfig[] {
  return Object.values(OAUTH_PROVIDERS).filter(p => !!p.clientId);
}
